import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { tasksAPI, projectsAPI } from '../utils/api';
import { useProjectPermissions as usePermissions } from '../hooks/usePermissions';

import { useToast, apiError } from '../components/Toast';
import { useMaster } from '../hooks/useMasters';
import { BOARD_ORDER, STATUS_STYLE } from '../utils/workStatus';

// Must mirror tasks_status_check exactly, or a drag produces a constraint
// violation and any task in an unlisted status renders in no column at all.
// The board is driven by Masters, but the COLUMN KEY must stay the value the
// database stores — a drop writes `status: key`, and tasks_status_check will
// reject anything else. So an Admin relabelling "Hold" to "Waiting" changes
// the heading only; the card still moves to the stored value 'Hold'.
//
// Colour is keyed on the VALUE too, so relabelling never loses the colour.
// Both the colours and the column order come from utils/workStatus, so the
// board cannot offer a status the database would reject.
const STATUS_COLOR = Object.fromEntries(
  Object.entries(STATUS_STYLE).map(([k, v]) => [k, v.dot]));
const COLUMNS_FALLBACK = BOARD_ORDER.map(v => ({ value:v, label:v }));

const PRIORITY_COLOR = {
  Critical: { bg:'rgba(239,68,68,.12)',  text:'#ef4444' },
  High:     { bg:'rgba(249,115,22,.12)', text:'#f97316' },
  Medium:   { bg:'rgba(46,83,159,.12)',  text:'#2E539F' },
  Low:      { bg:'rgba(100,116,139,.1)', text:'#64748b' },
};

export default function KanbanPage() {
  // Columns follow Masters: the label is the Admin's, the key is the DB value.
  const COLUMNS = useMaster('task_status', COLUMNS_FALLBACK)
    .map(o => ({ key:o.value, label:o.label, color: STATUS_COLOR[o.value] || '#64748b' }));
  const toast = useToast();
  const navigate   = useNavigate();
  const [tasks,    setTasks]    = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectFilter, setProjectFilter] = useState('');
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [draggingId,  setDraggingId]  = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);

  // Permission check scoped to the selected project
  const { can } = usePermissions(projectFilter || null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [tRes, pRes] = await Promise.all([
        tasksAPI.getAll({ ...(projectFilter ? { project_id: projectFilter } : {}), limit: 0 }),
        projectsAPI.getAll({ limit: 0 }),
      ]);
      setTasks(tRes.data   || []);
      setProjects(pRes.data || []);
    } catch(e) {
      setError(e.response?.data?.message || 'Failed to load tasks');
    } finally { setLoading(false); }
  }, [projectFilter]);

  useEffect(() => { load(); }, [load]);

  const handleDrop = async (newStatus) => {
    setDragOverCol(null);
    if (!draggingId) return;
    const task = tasks.find(t => t.id === draggingId);
    if (!task || task.status === newStatus) { setDraggingId(null); return; }

    if (projectFilter && !can('create_tasks')) {
      toast.warning('You do not have permission to move tasks on this project.');
      setDraggingId(null); return;
    }

    // Optimistic update
    setTasks(prev => prev.map(t => t.id === draggingId ? { ...t, status: newStatus } : t));
    setDraggingId(null);
    try {
      await tasksAPI.update(draggingId, { status: newStatus });
      toast.success('Task updated');
    } catch(e) {
      toast.error(apiError(e, 'Failed to update task status'));
      load(); // revert on failure
    }
  };

  const selectedProjectName = projects.find(p => p.id === projectFilter)?.name;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="page-title">Kanban Board</h1>
          <p className="text-sm mt-0.5" style={{ color:'var(--text-secondary)' }}>
            {selectedProjectName
              ? `Showing tasks for ${selectedProjectName}`
              : 'Drag a card left or right to change its status'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold uppercase tracking-wider" style={{ color:'var(--text-secondary)' }}>Project</label>
          <select className="select text-sm w-52" value={projectFilter} onChange={e => setProjectFilter(e.target.value)}>
            <option value="">All Projects</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 rounded-xl text-sm" style={{ background:'rgba(239,68,68,.08)', color:'#ef4444', border:'1px solid rgba(239,68,68,.2)' }}>
          ⚠ {error}
        </div>
      )}

      {/* Stats row */}
      {!loading && (
        <div className="flex items-center gap-4 text-xs" style={{ color:'var(--text-secondary)' }}>
          {COLUMNS.map(col => (
            <span key={col.key} className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full" style={{ background: col.color }}/>
              <span style={{ color:'var(--text)' }}>{tasks.filter(t => t.status === col.key).length}</span>
              {col.label}
            </span>
          ))}
          <span className="ml-auto">Total: {tasks.length} task{tasks.length !== 1 ? 's' : ''}</span>
        </div>
      )}

      {/* Board */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin"/>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {COLUMNS.map(col => {
            const colTasks = tasks.filter(t => t.status === col.key);
            const isDragOver = dragOverCol === col.key;
            return (
              <div key={col.key}
                onDragOver={e => { e.preventDefault(); setDragOverCol(col.key); }}
                onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverCol(null); }}
                onDrop={e => { e.preventDefault(); handleDrop(col.key); }}
                className="rounded-2xl transition-all"
                style={{
                  background: isDragOver ? `${col.color}12` : 'var(--surface-alt)',
                  border: `2px solid ${isDragOver ? col.color : 'transparent'}`,
                  minHeight: 420,
                  padding: '12px',
                }}>

                {/* Column header */}
                <div className="flex items-center justify-between mb-3 px-1">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: col.color }}/>
                    <p className="font-semibold text-sm" style={{ color:'var(--text)' }}>{col.label}</p>
                  </div>
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                    style={{ background: `${col.color}20`, color: col.color }}>
                    {colTasks.length}
                  </span>
                </div>

                {/* Cards */}
                <div className="space-y-2.5">
                  {colTasks.map(task => {
                    const overdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== 'Completed';
                    const pColor = PRIORITY_COLOR[task.priority] || PRIORITY_COLOR.Medium;
                    return (
                      <div key={task.id}
                        draggable
                        onDragStart={e => { setDraggingId(task.id); e.dataTransfer.effectAllowed = 'move'; }}
                        onDragEnd={() => setDraggingId(null)}
                        onClick={() => navigate(`/tasks/${task.id}`)}
                        className="card cursor-grab active:cursor-grabbing hover:shadow-md transition-all select-none"
                        style={{
                          borderLeft: `3px solid ${col.color}`,
                          opacity: draggingId === task.id ? 0.45 : 1,
                          transform: draggingId === task.id ? 'rotate(2deg)' : 'none',
                          padding: '10px 12px',
                        }}>

                        {/* Project name (shown when All Projects) */}
                        {!projectFilter && task.project_name && (
                          <p className="text-xs mb-1.5 truncate font-medium" style={{ color:'#2E539F' }}>
                            {task.project_name}
                          </p>
                        )}

                        {/* Task title */}
                        <p className="text-sm font-semibold leading-snug mb-2" style={{ color:'var(--text)' }}>
                          {task.title}
                        </p>

                        {/* Priority + due date */}
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          {task.priority && (
                            <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                              style={{ background: pColor.bg, color: pColor.text }}>
                              {task.priority}
                            </span>
                          )}
                          {task.due_date && (
                            <span className={`text-xs flex items-center gap-1 ${overdue ? 'font-bold' : ''}`}
                              style={{ color: overdue ? '#ef4444' : 'var(--text-secondary)' }}>
                              {overdue ? '⚠' : '📅'}
                              {new Date(task.due_date).toLocaleDateString('en-GB', { day:'2-digit', month:'short' })}
                            </span>
                          )}
                        </div>

                        {/* Assignee */}
                        {task.assigned_to_name && (
                          <div className="flex items-center gap-1.5 mt-2 pt-2 border-t" style={{ borderColor:'var(--border)' }}>
                            <div className="w-5 h-5 rounded-full text-white text-xs font-bold flex items-center justify-center flex-shrink-0"
                              style={{ background: task.assigned_to_color || '#2E539F' }}>
                              {task.assigned_to_name[0].toUpperCase()}
                            </div>
                            <span className="text-xs truncate" style={{ color:'var(--text-secondary)' }}>
                              {task.assigned_to_name}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Empty state */}
                  {colTasks.length === 0 && !isDragOver && (
                    <div className="flex flex-col items-center justify-center py-10 opacity-60">
                      <p className="text-2xl mb-1">
                        {col.key === 'Completed' ? '🎉' : col.key === 'In Progress' ? '⚡' : col.key === 'Hold' ? '⏸' : col.key === 'Skipped' ? '⤼' : '📋'}
                      </p>
                      <p className="text-xs" style={{ color:'var(--text-secondary)' }}>No tasks here</p>
                    </div>
                  )}

                  {/* Drop zone hint */}
                  {isDragOver && (
                    <div className="rounded-xl border-2 border-dashed py-6 text-center text-xs"
                      style={{ borderColor: col.color, color: col.color }}>
                      Drop here → {col.label}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Empty state */}
      {!loading && tasks.length === 0 && !error && (
        <div className="text-center py-16">
          <p className="text-4xl mb-3">📋</p>
          <p className="font-semibold text-lg" style={{ color:'var(--text)' }}>No tasks found</p>
          <p className="text-sm mt-1" style={{ color:'var(--text-secondary)' }}>
            {projectFilter ? 'This project has no tasks yet.' : 'Select a project or create your first task.'}
          </p>
        </div>
      )}
    </div>
  );
}
